import type { Node } from '../pages/labs/home/hooks/useNodes';
import { generateCypherQueryFromConcreteTypes } from './GraphQLFragmentGenerator';
import type { TypesByName, GraphQLSchemaVariant } from './GraphQLSchemaAtoms';
import type { ReturnLabelEntry } from './LabsAtoms';

type NodeMapping = Pick<Node, 'name'> & {
	aliases?: { name: string }[];
	graphQL?: Node['graphQL'];
	graphQLTypes?: string[];
};

/**
 * Converts Cypher node labels to GraphQL types using the nodes mapping.
 *
 * @param labels - Array of label names from the Cypher query
 * @param nodesMap - Map of node types with their GraphQL hydration information
 * @param schemaVariant - Which schema variant is active
 * @returns Array of GraphQL type names
 */
export function convertLabelsToGraphQLTypes(
	labels: string[],
	nodesMap: Record<string, NodeMapping>,
	schemaVariant: GraphQLSchemaVariant = 'internal',
	typesByName?: TypesByName,
): string[] {
	const graphQLTypes: string[] = [];

	for (const label of labels) {
		// Find the node entry that matches this label by name OR by alias
		const nodeEntry = Object.values(nodesMap).find(
			(node) => node.name === label || node.aliases?.some((alias) => alias.name === label),
		);

		if (nodeEntry) {
			if (schemaVariant === 'external') {
				// Public labels take precedence over internal hydration names.
				if (!typesByName || typesByName[label.toLowerCase()]) {
					graphQLTypes.push(label);
				} else {
					const candidates = [
						...(nodeEntry.graphQLTypes ?? []),
						...(nodeEntry.aliases?.map((alias) => alias.name) ?? []),
					];
					graphQLTypes.push(...candidates.filter((name) => typesByName[name.toLowerCase()]));
				}
			} else if (nodeEntry.graphQL?.hydration?.type) {
				// For Labs (Internal Schema), use the configured hydration types
				graphQLTypes.push(...nodeEntry.graphQL.hydration.type);
			}
		}
	}

	// Return unique types
	return Array.from(new Set(graphQLTypes));
}

/**
 * Generates a GraphQL query based on the return labels from a Cypher query.
 *
 * @param returnLabels - Array of return label entries from the LSP
 * @param nodesMap - Map of node types with their GraphQL hydration information
 * @param typesByName - GraphQL schema types
 * @param limit - Limit for the GraphQL edges connection
 * @param username - Optional username to include in the query name
 * @param schemaVariant - Which schema variant is active
 * @returns Generated GraphQL query or null if unable to generate
 */
export function generateGraphQLQueryFromReturnLabels(
	returnLabels: ReturnLabelEntry[],
	nodesMap: Record<string, NodeMapping>,
	typesByName: TypesByName,
	limit: number,
	username?: string,
	schemaVariant: GraphQLSchemaVariant = 'internal',
	queryNameSuffix?: string,
): { query: string; fragment: string; warnings: string[] } | null {
	if (!returnLabels || returnLabels.length === 0) {
		return null;
	}

	// Collect all labels from all return entries
	const allLabels: string[] = [];
	for (const entry of returnLabels) {
		if (entry.labels && entry.labels.length > 0) {
			allLabels.push(...entry.labels);
		}
	}

	if (allLabels.length === 0) {
		return null;
	}

	// Convert labels to GraphQL types
	const graphQLTypes = convertLabelsToGraphQLTypes(allLabels, nodesMap, schemaVariant, typesByName);

	if (graphQLTypes.length === 0) {
		return null;
	}

	// Generate the query using the concrete types (single query API)
	return generateCypherQueryFromConcreteTypes(
		graphQLTypes,
		typesByName,
		{
			depth: 2,
			includeTypename: true,
			skipDeprecated: true,
			skipExperimental: true,
			autoSelectIdForObjects: true,
			schemaVariant,
			queryNamePrefix: schemaVariant === 'external' ? 'QueryBuilder_OpName_' : 'TwgLabs',
			queryNameSuffix,
		},
		limit,
		username,
	);
}
